import React, { useState, useEffect } from 'react';

interface GalleryImage {
  filename: string;
  path: string;
  caption?: string;
}

const Gallery: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // List of image filenames from the gallery directory
    const imageFilenames = [
      'LI2025 (00).jpg',
      'LI2025 (00a).jpg',
      'LI2025 (00b).JPG',
      'LI2025 (00c).jpg',
      'LI2025 (01).jpg',
      'LI2025 (03).jpg',
      'LI2025 (04).jpg',
      'LI2025 (05).jpg',
      'LI2025 (06).jpg',
      'LI2025 (08).jpg',
      'LI2025 (10).jpg',
      'LI2025 (11).jpg',
      'LI2025 (12).jpg',
      'LI2025 (14).jpg',
      'LI2025 (15).jpg',
      'LI2025 (16).jpg',
      'LI2025 (18).jpg',
      'LI2025 (19).jpg',
      'LI2025 (20).jpg',
      'LI2025 (21).jpg',
      'LI2025 (23).jpg',
      'LI2025 (24).jpg',
      'LI2025 (25).jpg',
      'LI2025 (28).jpg',
      'LI2025 (29).jpg',
      'LI2025 (30).jpg',
      'LI2025 (31).jpg',
      'LI2025 (33).jpg',
      'LI2025 (34).jpg',
      'LI2025 (35).jpg',
      'LI2025 (38).jpg',
      'LI2025 (39).jpg',
      'LI2025 (40).jpg',
      'LI2025 (42).jpg',
      'LI2025 (43).jpg',
      'LI2025 (44).jpg',
      'LI2025 (45).jpg',
      'LI2025 (47).jpg',
      'LI2025 (49).jpg',
      'LI2025 (51).jpg',
      'LI2025 (52).jpg',
      'LI2025 (53).jpg',
      'LI2025 (54).jpg',
      'LI2025 (55).jpg',
      'LI2025 (56).jpg',
      'LI2025 (57).jpg',
      'LI2025 (58).jpg',
      'LI2025 (59).jpg',
      'LI2025 (61).jpg',
      'LI2025 (62).jpg',
      'LI2025 (63).jpg',
      'LI2025 (64).jpg',
      'LI2025 (65).jpg',
      'LI2025 (66).jpg',
      'LI2025 (67).jpg',
      'LI2025 (68).jpg',
      'LI2025 (69).jpg',
      'LI2025 (70).jpg',
      'LI2025 (70b).jpg',
      'LI2025 (71).jpg',
      'LI2025 (72).jpg',
      'LI2025 (73).jpg',
      'LI2025 (74).jpg',
      'LI2025 (74b).jpg',
      'LI2025 (75).jpg',
      'LI2025 (76).jpg',
      'LI2025 (77).jpg',
      'LI2025 (78).jpg',
      'LI2025 (79).jpg',
      'LI2025 (80).jpg',
      'LI2025 (81).jpg',
    ];

    // Convert filenames to gallery images for the 2025 edition
    const galleryImages = imageFilenames.map((filename, index) => ({
      filename,
      path: `/final_pictures/${filename}`,
      caption: `Language Intelligence 2025 - Photo ${index + 1}`,
    }));

    setImages(galleryImages);
    setLoading(false);
  }, []);

  const openLightbox = (image: GalleryImage) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden'; // Prevent scrolling
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (!selectedImage) return;
    
    const currentIndex = images.findIndex(img => img.filename === selectedImage.filename);
    let newIndex: number;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    } else {
      newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSelectedImage(images[newIndex]);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedImage) return;
      
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateImage('prev');
      if (e.key === 'ArrowRight') navigateImage('next');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12 bg-gradient-to-br from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-gradient-to-br from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
            Language Intelligence 2025 - Photo Gallery
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Relive moments from the 2025 edition of the Language Intelligence Conference.
          </p>
          <div className="mt-4 text-sm text-gray-500">
            {images.length} photos
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="group relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-gray-200"
              onClick={() => openLightbox(image)}
            >
              <img
                src={image.path}
                alt={image.caption || image.filename}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
              aria-label="Close"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Previous button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('prev');
              }}
              className="absolute left-4 text-white hover:text-gray-300 transition-colors z-50"
              aria-label="Previous image"
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Next button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('next');
              }}
              className="absolute right-4 text-white hover:text-gray-300 transition-colors z-50"
              aria-label="Next image"
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Image */}
            <div
              className="max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.path}
                alt={selectedImage.caption || selectedImage.filename}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
